import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import { ProductKeys3P } from '../../constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import { type UseIsUserOAuthed } from '../../ui/oauth-warning-wrapper';
import { ThirdPartyQuerySuggestions } from '../../ui/third-party-query-suggestions';
import {
	dismissThirdPartyQuerySuggestions,
	isThirdPartyQuerySuggestionsDismissed,
} from '../../ui/third-party-query-suggestions/utils';
import { onThirdPartyQuerySuggestionClicked } from '../events/third-party-query-suggestion-clicked';
import { onThirdPartyQuerySuggestionsDismissed } from '../events/third-party-query-suggestions-dismissed';
import { onThirdPartyQuerySuggestionsShown } from '../events/third-party-query-suggestions-shown';
import { serveInline3PQuerySuggestionsPhase1 } from '../experiments';
import { isEnglishLocale } from '../locale';

import {
	GOOGLE_DRIVE_DYNAMIC_SUGGESTIONS,
	GOOGLE_DRIVE_STATIC_SUGGESTIONS,
} from './suggestions-data';
import {
	type ProductSuggestionsConfig,
	type QuerySuggestion,
	type UseAnalytics,
	type UseSearchActions,
} from './types';
import { useProductSuggestions } from './use-product-suggestions';

// Redacts the current query within a dynamic suggestion to prevent logging UGC
const sanitizeSuggestion = (suggestion: string, currentQuery?: string): string => {
	return currentQuery ? suggestion.replace(`"${currentQuery}"`, '"[query]"') : suggestion;
};

const shouldShowContainer = ({
	isDismissed,
	displayedConfig,
	userNeedsOAuth,
	locale,
	thirdPartyConfigs,
}: {
	isDismissed: boolean;
	displayedConfig: ProductSuggestionsConfig | undefined;
	userNeedsOAuth: boolean | undefined;
	locale: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}): boolean => {
	// Don't show if user has dismissed, config is missing, or user isn't authenticated
	if (isDismissed || !displayedConfig || userNeedsOAuth !== false) {
		return false;
	}

	// Don't show if locale isn't English
	if (!isEnglishLocale(locale)) {
		return false;
	}

	// Don't show if connector creation date is missing
	const connectorCreatedAt = thirdPartyConfigs[displayedConfig.productKey]?.connectorCreatedAt;
	if (!connectorCreatedAt) {
		return false;
	}

	// Don't show if the connector was created less than 2 weeks ago
	const twoWeeksAgo = new Date();
	twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
	return new Date(connectorCreatedAt) <= twoWeeksAgo;
};

interface ThirdPartyQuerySuggestionsContainerProps {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	useIsUserOAuthed: UseIsUserOAuthed;
	useSearchActions: UseSearchActions;
	useAnalytics: UseAnalytics;
	currentQuery?: string;
}

export const ThirdPartyQuerySuggestionsContainer = ({
	thirdPartyConfigs,
	useIsUserOAuthed,
	useSearchActions,
	useAnalytics,
	currentQuery,
}: ThirdPartyQuerySuggestionsContainerProps): React.JSX.Element | null => {
	const { locale } = useIntl();
	const [isDismissed, setIsDismissed] = useState(isThirdPartyQuerySuggestionsDismissed());
	const { setInputQuery, selectProductFilter } = useSearchActions();
	const { fireAnalyticsEvent } = useAnalytics();

	// For now, only Google Drive is supported
	const displayedProductKey = ProductKeys3P.GoogleDrive;
	const { userNeedsOAuth } = useIsUserOAuthed(displayedProductKey);

	const suggestions = useProductSuggestions(
		GOOGLE_DRIVE_STATIC_SUGGESTIONS,
		GOOGLE_DRIVE_DYNAMIC_SUGGESTIONS,
		currentQuery,
	);

	const displayedConfig: ProductSuggestionsConfig = {
		productKey: displayedProductKey,
		suggestions,
	};

	if (
		!shouldShowContainer({
			isDismissed,
			displayedConfig,
			userNeedsOAuth,
			locale,
			thirdPartyConfigs,
		})
	) {
		return null;
	}

	const inline3PQuerySuggestionsPhase1ExperimentTreatment = serveInline3PQuerySuggestionsPhase1();

	if (inline3PQuerySuggestionsPhase1ExperimentTreatment === 'control') {
		return null;
	}

	const handleSuggestionsShown = () => {
		fireAnalyticsEvent(
			onThirdPartyQuerySuggestionsShown({
				productKey: displayedConfig.productKey,
			}),
		);
	};

	const handleSuggestionClick = (clickedSuggestion: QuerySuggestion) => {
		fireAnalyticsEvent(
			onThirdPartyQuerySuggestionClicked({
				productKey: displayedConfig.productKey,
				suggestionType: clickedSuggestion.suggestionType,
				suggestion: sanitizeSuggestion(clickedSuggestion.text, currentQuery),
			}),
		);
		setInputQuery(clickedSuggestion.text);
		selectProductFilter(displayedConfig.productKey);
	};

	const handleDismiss = () => {
		fireAnalyticsEvent(
			onThirdPartyQuerySuggestionsDismissed({ productKey: displayedConfig.productKey }),
		);
		dismissThirdPartyQuerySuggestions();
		setIsDismissed(true);
	};

	return (
		<ThirdPartyQuerySuggestions
			suggestions={displayedConfig.suggestions}
			productKey={displayedConfig.productKey}
			thirdPartyConfigs={thirdPartyConfigs}
			onSuggestionsShown={handleSuggestionsShown}
			onSuggestionClicked={handleSuggestionClick}
			onDismiss={handleDismiss}
		/>
	);
};
